<template>
	<div role="listitem" :class="{divided: props.divided, 'is-clickable': isClickable}" class="resource-list-item relative block" @click="handleClick">
		<component :is="tag" v-bind="linkProps" class="flex px-16 lg:px-24" :class="{'items-center': props.centered, 'py-16 md:py-20': props.divided && !$slots.below, 'pt-16 md:pt-20 pb-8': props.divided && $slots.below}">
			<div v-if="props.image || $slots.image" class="mr-14 sm:mr-20">
				<div v-if="props.image" class="mt-2">
					<avatar :size="props.imageWidth" :src="props.image" :round="props.imageRatio === 1" :ratio="props.imageRatio" contain/>
				</div>
				<slot name="image"/>
			</div>

			<div class="flex-grow-x-1 overflow-hidden">
				<div class="flex overflow-hidden" :class="{'flex-column md:flex-row': props.breakRight, 'mb-4': props.description || $slots.default, 'items-center': props.centered && !props.breakRight, 'md:items-center': props.centered && props.breakRight}">
					<div class="flex flex-column overflow-hidden flex-grow-1" :class="{'justify-center': props.centered}">
						<p v-if="props.title" class="text-sm mb-1 leading-spaced" :class="{'text-heading-color font-heading font-semi-bold': !props.titleColor}" :style="{color: props.titleColor}">{{ props.title }}<slot name="title_suffix"/></p>
						<slot name="title"/>
						<p v-if="props.subtitle" class="text-xs font-heading text-text-color mb-0 leading-wide truncate">{{ props.subtitle }}</p>
					</div>
					<div v-if="$slots.right" class="flex justify-end text-right" :class="[{'items-center': props.centered}, props.breakRight ? 'mt-8 md:ml-8 md:mt-0 w-full md:w-auto' : 'ml-8']">
						<slot name="right"/>
					</div>
				</div>

				<p v-if="props.description" class="text-subtext-color leading-wide whitespace-pre-wrap" :class="[props.focusedDescription ? 'text-sm' : 'text-xs', $slots.default ? 'mb-8' : 'mb-0']" v-html="props.description"/>

				<slot/>

			</div>
		</component>

		<slot name="below"/>
	</div>
</template>

<script setup lang="ts">
import {type LinkableProps, useLinkable} from "../../../composables/useLinkable";
import {computed} from "vue";
import type {Image} from "@spoferan/spoferan-ts-core";

const props = withDefaults(defineProps<{
	title?: string,
	subtitle?: string,
	description?: string,

	titleColor?: string,

	// Whether the description is the main focus point of the item and shall be displayed in a larger font
	focusedDescription?: boolean,

	// Whether the resource item can be clicked and shall receive a hover effect
	clickable?: boolean,

	// Whether the item shall receive a disabled look and no hover effect
	disabled?: boolean,

	// Whether the resource item shall get additional spacing and multiple items shall be divided with a border
	divided?: boolean,

	// Whether to center all items vertically
	centered?: boolean,

	// Whether to break the right slot below the main slot on mobile devices
	breakRight?: boolean,

	// The src of an image to display on the image slot
	image?: string | Image,
	imageRatio?: number,
	imageWidth?: number,
} & LinkableProps>(), {
	imageWidth: 40,
	imageRatio: 1
});

const emit = defineEmits(['click']);

const {linkProps} = useLinkable(props);

const isClickable = computed(() => {
	return props.clickable || linkProps.value;
});

const tag = computed(() => {
	if (linkProps.value) {
		return resolveComponent('NuxtLink');
	}

	return 'div';
});

function handleClick(event) {
	if (!props.disabled) {
		emit('click', event)
	}
}
</script>

<style>
/*********************
  Inputs
**********************/
.resource-list-item.is-clickable:hover {
  cursor: pointer;
  background-color: #fafafa;
}
</style>